import type { BuyerFundingFlowReview } from "../../domain/buyerFundingFlowSmoke";
import { instructionVersionLabel } from "../../domain/buyerFundingAcknowledgment";
import type { PaymentRail } from "../../domain/types";
import type {
  BuyerFundingFlowAddressValidation,
  BuyerFundingFlowDocuSignAck,
  BuyerFundingFlowInstructionAck,
  BuyerFundingFlowPaymentInitiation,
  BuyerFundingFlowPlaidEvidence
} from "./types";

export function recordFlowPaymentInitiation(
  review: BuyerFundingFlowReview,
  initiation: BuyerFundingFlowPaymentInitiation
): BuyerFundingFlowReview {
  return {
    ...review,
    paymentInitiation: {
      ...review.paymentInitiation,
      evidenceType: "Buyer-reported initiation",
      rail: initiation.rail as PaymentRail,
      initiatedAmount: { amountCents: initiation.amountCents, currency: "USD" },
      initiatedAt: initiation.initiatedAt,
      initiatedBy: review.buyerParties[0]?.name || "Buyer",
      reference: initiation.reference || review.fileNumber,
      fileMemo: review.fileNumber,
      sourceDocumentId: "Buyer funding flow payment initiation",
      limitation: initiation.limitation
    },
    buyerFundingFlow: {
      ...review.buyerFundingFlow,
      paymentInitiatedAt: initiation.initiatedAt,
      paymentInitiationReference: initiation.reference
    },
    updatedAt: initiation.initiatedAt
  };
}

function sourceEvidenceFromPlaid(plaid: BuyerFundingFlowPlaidEvidence):
  | {
      evidenceType: string;
      provider: "plaid" | "manual";
    }
  | undefined {
  if (plaid.mode === "skipped") return undefined;
  if (plaid.mode === "manual") {
    return { evidenceType: "Manual source-account evidence", provider: "manual" };
  }
  if (plaid.mode === "live" || plaid.mode === "simulated") {
    return { evidenceType: "Plaid account owner evidence", provider: "plaid" };
  }
  return undefined;
}

export function recordFlowPlaidEvidence(
  review: BuyerFundingFlowReview,
  plaid: BuyerFundingFlowPlaidEvidence
): BuyerFundingFlowReview {
  const mapped = sourceEvidenceFromPlaid(plaid);
  if (!mapped) return review;
  const { evidenceType, provider } = mapped;
  return {
    ...review,
    sourceAccountEvidence: {
      ...review.sourceAccountEvidence,
      evidenceType,
      provider,
      recordedAt: plaid.connectedAt,
      bankName: plaid.institutionName,
      accountLast4: plaid.accountLast4,
      ownerName: plaid.ownerName,
      ownerMatchResult: plaid.ownerMatchHint?.includes("did not match") ? "Did not match" : "Matched",
      balanceRecorded: false,
      sourceParty: plaid.ownerName,
      providerResultId: plaid.itemId,
      limitation: [plaid.limitation, plaid.ownerMatchHint].filter(Boolean).join(" ")
    },
    buyerFundingFlow: {
      ...review.buyerFundingFlow,
      buyerViewedAt: review.buyerFundingFlow.buyerViewedAt || plaid.connectedAt
    },
    updatedAt: plaid.connectedAt
  };
}

export function recordFlowAddressValidation(
  review: BuyerFundingFlowReview,
  validation: BuyerFundingFlowAddressValidation
): BuyerFundingFlowReview {
  return {
    ...review,
    propertyAddress: validation.formattedAddress || review.propertyAddress,
    buyerFundingFlow: {
      ...review.buyerFundingFlow,
      buyerViewedAt: review.buyerFundingFlow.buyerViewedAt || validation.validatedAt,
      addressValidationAt: validation.validatedAt,
      addressValidationPlaceId: validation.placeId,
      addressValidationVerdict: validation.verdict
    },
    updatedAt: validation.validatedAt
  };
}

export function recordFlowDocuSignAck(
  review: BuyerFundingFlowReview,
  ack: BuyerFundingFlowDocuSignAck
): BuyerFundingFlowReview {
  const statement = `DocuSign Buyer Funding Acknowledgment for instruction version ${ack.instructionVersionLabel}.`;
  return {
    ...review,
    buyerAcknowledgment: {
      ...review.buyerAcknowledgment,
      type: "Buyer Funding Acknowledgment — Signed",
      partyName: ack.signerName,
      acknowledgedInstructionVersion: ack.instructionVersionLabel,
      acknowledgedAt: ack.signedAt,
      channel: ack.mode === "live" ? "DocuSign embedded signing" : "DocuSign simulated signing",
      sourceDocumentId: `DocuSign envelope ${ack.envelopeId}`,
      recordedBy: ack.signerName,
      limitation: ack.limitation
    },
    buyerFundingFlow: {
      ...review.buyerFundingFlow,
      buyerAcknowledgedAt: ack.signedAt,
      acknowledgmentStatement: statement,
      buyerViewedAt: review.buyerFundingFlow.buyerViewedAt || ack.signedAt,
      docuSignEnvelopeId: ack.envelopeId,
      docuSignSignedAt: ack.signedAt,
      docuSignInstructionHash: ack.instructionHash,
      docuSignDocumentHash: ack.documentHash,
      docuSignSignerEmail: ack.signerEmail,
      docuSignSignedDocumentBase64: ack.signedDocumentBase64
    },
    updatedAt: ack.signedAt
  };
}

export function recordFlowInstructionAck(
  review: BuyerFundingFlowReview,
  ack: BuyerFundingFlowInstructionAck
): BuyerFundingFlowReview {
  const statement =
    ack.method === "passkey"
      ? `Passkey acknowledgment for instruction version ${instructionVersionLabel(review.fileNumber, ack.instructionVersion)}`
      : `Checkbox acknowledgment for instruction version ${instructionVersionLabel(review.fileNumber, ack.instructionVersion)}`;
  return {
    ...review,
    buyerAcknowledgment: {
      ...review.buyerAcknowledgment,
      type: ack.method === "passkey" ? "Passkey instruction acknowledgment" : "Known-entry acknowledgment",
      acknowledgedInstructionVersion: instructionVersionLabel(review.fileNumber, ack.instructionVersion),
      acknowledgedAt: ack.acknowledgedAt,
      limitation: ack.limitation
    },
    buyerFundingFlow: {
      ...review.buyerFundingFlow,
      buyerAcknowledgedAt: ack.acknowledgedAt,
      acknowledgmentStatement: statement,
      instructionAckMethod: ack.method,
      instructionAckCredentialId: ack.passkeyCredentialId
    },
    updatedAt: ack.acknowledgedAt
  };
}
